import { ReactNode, useEffect, useRef, useState } from "react";

/** A single item in a {@link Dropdown}. `disabled` items render dimmed and can't be picked.
 *  `icon` renders a leading element (e.g. an avatar chip) and `sublabel` a dimmed second line —
 *  both optional, so plain text dropdowns are unaffected. */
export type DropdownOption = { value: string; label: string; disabled?: boolean; icon?: ReactNode; sublabel?: string };

type DropdownProps = {
  /** Selected option value. Pass "" (with a `placeholder`) for no selection. */
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  /** Accessible name for the trigger + listbox (there's no visible <label>). */
  ariaLabel?: string;
  disabled?: boolean;
  /** Show a "Loading…" label on the trigger while options are still being fetched. */
  loading?: boolean;
  size?: "sm" | "md";
  /** Which edge the menu aligns to when it's wider than the trigger. Defaults to "left". */
  align?: "left" | "right";
  /** Applied to the wrapper — set width / flex here (the trigger is always w-full). */
  className?: string;
  /** Give the trigger a persistent orange (brand) border instead of the neutral one — used where
   *  the dropdown is a primary action (e.g. the skill-page install-target picker). */
  accent?: boolean;
  /** Allow clearing the selection: re-picking the current option toggles it off
   *  (emits `onChange("")`, so the placeholder returns). Off by default — leave it
   *  off for required-value dropdowns (access level, role, …). */
  clearable?: boolean;
  /** Show a search box at the top of the menu that filters options by label/sublabel
   *  (case-insensitive). Useful when the list is long (e.g. "pick a skill you own"). */
  searchable?: boolean;
};

// Per-size padding + icon dimensions, keyed by the `size` prop.
const SIZES = {
  sm: { trigger: "py-1 pl-2.5 pr-2 text-xs", option: "px-2.5 py-1.5 text-xs", icon: 12 },
  md: { trigger: "py-2 pl-3 pr-2.5 text-sm", option: "px-3 py-1.5 text-sm", icon: 14 },
};

/**
 * Reusable themed dropdown — a drop-in replacement for the native `<select>`, whose
 * popup is OS-rendered (a white system menu on macOS) and can't be styled to match
 * our theme. Renders a fully themed menu *below* the trigger.
 *
 * Features:
 * - Theme-aware in light & dark (built on the `--bg-elev` / `--border` / `--shadow-card`
 *   CSS vars and the `text-text` / `text-primary` tokens — no per-theme props needed).
 * - Closes on outside click, Escape, or Tab; keyboard nav via ArrowUp/Down + Enter/Space
 *   (skipping disabled options), with the current selection pre-highlighted on open.
 * - ARIA listbox roles for screen readers.
 *
 * The parent owns the selected `value`; this component is controlled and holds only
 * open/highlight UI state.
 *
 * @example
 * <Dropdown
 *   value={role}
 *   onChange={setRole}
 *   ariaLabel="Member role"
 *   size="sm"
 *   options={["admin", "write", "read"].map((r) => ({ value: r, label: r }))}
 * />
 */

export default function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  ariaLabel,
  disabled,
  loading,
  size = "md",
  align = "left",
  className,
  accent = false,
  clearable = false,
  searchable = false,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  // Keyboard-highlighted option index (also driven by mouse hover). -1 = none.
  const [activeIdx, setActiveIdx] = useState(-1);
  // Free-text filter (searchable only); indexes below are into `filtered`, not `options`.
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null); // wrapper, for outside-click detection
  const inputRef = useRef<HTMLInputElement>(null); // search box, to focus on open
  const sz = SIZES[size];
  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered =
    searchable && q
      ? options.filter((o) => `${o.label} ${o.sublabel ?? ""}`.toLowerCase().includes(q))
      : options;

  // Close when a click (mousedown) lands outside the wrapper.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // On open, reset the filter and highlight the current selection so keyboard nav
  // starts there; focus the search box when searchable.
  useEffect(() => {
    if (!open) return;
    if (searchable) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // As the query narrows the list, move the highlight to the first match.
  useEffect(() => {
    if (open && searchable) setActiveIdx(filtered.findIndex((o) => !o.disabled));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Select the option at `idx` (ignoring disabled/out-of-range) and close.
  // When `clearable`, re-picking the already-selected option toggles it off.
  function commit(idx: number) {
    const opt = filtered[idx];
    if (!opt || opt.disabled) return;
    onChange(clearable && opt.value === value ? "" : opt.value);
    setOpen(false);
  }

  // Move the highlight by `dir`, wrapping around and skipping disabled options.
  function move(dir: 1 | -1) {
    if (filtered.length === 0) return;
    setActiveIdx((i) => {
      let n = i;
      for (let step = 0; step < filtered.length; step++) {
        n = (n + dir + filtered.length) % filtered.length;
        if (!filtered[n].disabled) return n;
      }
      return i; // every other option disabled — stay put
    });
  }

  // Keyboard handling on the wrapper: open/navigate/commit/close.
  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Escape") { setOpen(false); return; }
    if (!open) {
      // Closed: Down / Enter / Space open the menu.
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); }
      return;
    }
    // Open: navigate the list, commit the highlight, or let Tab dismiss.
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Enter") { e.preventDefault(); commit(activeIdx); }
    // Space commits only when there's no search box — otherwise it types a space.
    else if (e.key === " " && !searchable) { e.preventDefault(); commit(activeIdx); }
    else if (e.key === "Tab") setOpen(false);
  }

  // Trigger shows: loading label → selected label → placeholder (dimmed).
  const triggerText = loading ? "Loading…" : selected ? selected.label : placeholder;
  const triggerMuted = !loading && !selected;

  return (
    <div ref={ref} className={`relative ${className ?? ""}`} onKeyDown={onKeyDown}>
      {/* Trigger: selected/placeholder label + chevron (rotates when open). */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-[var(--bg-elev)] font-medium text-text/80 outline-none transition-colors focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${
          accent ? "border-primary/40 hover:border-primary" : "border-[var(--border)] hover:border-primary/40"
        } ${sz.trigger}`}
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected?.icon && !loading && <span className="shrink-0">{selected.icon}</span>}
          <span className={`truncate ${triggerMuted ? "text-text/40" : ""}`}>{triggerText}</span>
        </span>
        <svg
          className={`shrink-0 text-text/40 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Menu: themed popup below the trigger; selected row is orange + checked. */}
      {open && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full z-30 mt-1.5 min-w-full overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--bg-elev)]`}
          style={{ boxShadow: "var(--shadow-card)" }}
        >
          {searchable && (
            <div className="border-b border-[var(--border)] p-1">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                aria-label={ariaLabel ? `Search ${ariaLabel}` : "Search"}
                className={`w-full rounded-md bg-transparent text-text outline-none placeholder:text-text/40 ${sz.option}`}
              />
            </div>
          )}
          <ul role="listbox" aria-label={ariaLabel} className="max-h-60 overflow-auto p-1">
            {filtered.length === 0 && (
              <li className={`text-text/40 ${sz.option}`}>{searchable && q ? "No matches" : "No options"}</li>
            )}
            {filtered.map((o, i) => {
            const isSel = o.value === value;
            const isActive = i === activeIdx;
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  disabled={o.disabled}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit(i)}
                  className={`flex w-full items-center justify-between gap-3 rounded-md text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${sz.option} ${
                    isSel ? "font-semibold text-primary" : "text-text/80"
                  } ${isActive ? "bg-black/[0.05] dark:bg-white/[0.06]" : ""}`}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    {o.icon && <span className="shrink-0">{o.icon}</span>}
                    <span className="min-w-0">
                      <span className="block truncate">{o.label}</span>
                      {o.sublabel && <span className="block truncate text-xs font-normal text-text/40">{o.sublabel}</span>}
                    </span>
                  </span>
                  {isSel && (
                    <svg className="shrink-0" width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
          </ul>
        </div>
      )}
    </div>
  );
}
